import React, { useCallback, useMemo, useRef } from "react";
import { Combobox, type ComboboxItem } from "@bloomberg/media.atomics";
import styles from "./FilterCombobox.module.css";
import {
  useIsLoading,
  useSectorData,
  useSelectedSectors,
  useWatchlistActions,
} from "~context/WatchlistContext";

const ALL_VALUE = "All";

// Bloomberg Industry Classification Standard (BICS) top-level sectors
const BICS_SECTORS = [
  "Basic Materials",
  "Communications",
  "Consumer Discretionary",
  "Consumer Non-cyclical",
  "Diversified",
  "Energy",
  "Financial",
  "Industrial",
  "Technology",
  "Utilities",
] as const;

const BICS_SECTOR_NAMES = new Set<string>(BICS_SECTORS);

const STATIC_ITEMS: ComboboxItem[] = BICS_SECTORS.map((name) => ({
  value: name,
  label: name,
}));

export default function SectorsFilter() {
  const containerRef = useRef<HTMLDivElement>(null);
  const isLoading = useIsLoading();
  const sectorData = useSectorData();
  const selectedSectors = useSelectedSectors();
  const { setSelectedSectors } = useWatchlistActions();

  const items: ComboboxItem[] = useMemo(() => {
    // Sectors from the watchlist that aren't in the standard BICS list
    const watchlistOnlySectors = sectorData
      .filter((name) => !BICS_SECTOR_NAMES.has(name))
      .map((name) => ({ value: name, label: name }));

    return [
      { value: ALL_VALUE, label: ALL_VALUE },
      ...STATIC_ITEMS,
      ...watchlistOnlySectors,
    ];
  }, [sectorData]);

  // "All" and specific sectors are mutually exclusive:
  // - Selecting "All" clears specific selections
  // - Selecting a specific sector while "All" is active removes "All"
  const handleValueChange = useCallback(
    (values: string[]) => {
      if (values.length === 0) {
        setSelectedSectors([]);

        return;
      }

      const hadAll = selectedSectors.includes(ALL_VALUE);
      const hasAll = values.includes(ALL_VALUE);

      if (hasAll && !hadAll) {
        // User just selected "All" — collapse to only "All"
        setSelectedSectors([ALL_VALUE]);
      } else if (hasAll && values.length > 1) {
        // User selected a specific sector while "All" was active — drop "All"
        setSelectedSectors(values.filter((v) => v !== ALL_VALUE));
      } else {
        setSelectedSectors(values);
      }
    },
    [setSelectedSectors, selectedSectors]
  );

  return (
    <div className={styles.filterContainer} ref={containerRef}>
      {/* Disabled during loading to prevent unmount/remount flicker on watchlist switch */}
      <Combobox
        labelText="All Sectors"
        items={items}
        value={selectedSectors}
        onValueChange={handleValueChange}
        disabled={isLoading}
        multiple
        portalTarget={containerRef}
      />
    </div>
  );
}
